import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  DataTable,
  createSortableHeader,
} from "../../../components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Role } from "../../../types";
import { useOperations } from "../../operations-management";
import { DeleteRoleDialog } from "../components/delete-role-dialog";
import { RoleFormDialog } from "../components/role-form-dialog";
import { useRoles } from "../hooks/use-role-queries";

export default function RoleManagementPage() {
  const [selectedOps, setSelectedOps] = useState<string>("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const { data: operations = [] } = useOperations();
  const { data: roles = [], isLoading, refetch } = useRoles(selectedOps);

  // Default to the first non-gestor operation once the list loads.
  const selectableOps = useMemo(
    () => operations.filter((op) => op.ops !== "gestor"),
    [operations]
  );

  // The selected ops's enabled screens — the role key-picker is limited to these.
  const enabledScreens = useMemo(
    () => operations.find((op) => op.ops === selectedOps)?.screens ?? [],
    [operations, selectedOps]
  );

  useEffect(() => {
    if (!selectedOps && selectableOps.length > 0) {
      setSelectedOps(selectableOps[0].ops);
    }
  }, [selectableOps, selectedOps]);

  const handleAdd = () => {
    setSelectedRole(null);
    setIsFormOpen(true);
  };

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setIsFormOpen(true);
  };

  const handleDelete = (role: Role) => {
    setSelectedRole(role);
    setIsDeleteOpen(true);
  };

  const columns: ColumnDef<Role>[] = [
    {
      accessorKey: "name",
      header: createSortableHeader("Nome"),
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    {
      accessorKey: "description",
      header: "Descrição",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {row.original.description || "—"}
        </div>
      ),
    },
    {
      id: "permissionKeys",
      header: "Permissões",
      enableSorting: false,
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">
          {row.original.permissionKeys.length} chave(s)
        </Badge>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const role = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleEdit(role)}>
                Editar função
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDelete(role)}
                className="text-red-600"
              >
                Excluir função
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="w-full py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Gerenciamento de Funções
          </h1>
          <p className="text-muted-foreground">
            Crie funções por operação e defina as permissões que elas concedem
          </p>
        </div>
        <Button onClick={handleAdd} disabled={!selectedOps}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Função
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm font-medium">Operação:</label>
        <Select value={selectedOps} onValueChange={setSelectedOps}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Selecione uma operação" />
          </SelectTrigger>
          <SelectContent>
            {selectableOps.map((op) => (
              <SelectItem key={op.ops} value={op.ops}>
                {op.color && (
                  <span
                    className="inline-block w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: op.color }}
                  />
                )}
                {op.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={roles}
        loading={isLoading}
        onRefresh={refetch}
        searchPlaceholder="Pesquise por nome..."
        searchableColumns={["name"]}
      />

      <RoleFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        role={selectedRole}
        ops={selectedOps}
        enabledScreens={enabledScreens}
        onSuccess={() => setSelectedRole(null)}
      />

      <DeleteRoleDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        role={selectedRole}
        onSuccess={() => setSelectedRole(null)}
      />
    </div>
  );
}
